import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { TinyHelpers } from '@coin/shared/core/util-helpers';
import { TemplateWithValueDirective } from '@coin/shared/core/util-directives';
import { MatIcon } from '@angular/material/icon';
import { ConfirmationDialogConfirm, ConfirmationDialogData } from '@coin/shared/core/util-models';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { V2DateInputComponent } from '../date-input/v2-date-input.component';
import { V2ButtonComponent } from '../button/v2-button.component';
import { V2CheckboxComponent } from '../checkbox/v2-checkbox.component';
import { V2DialogModule } from '../dialog-frame/v2-dialog.module';
import { V2DropdownComponent } from '../dropdown/v2-dropdown.component';
import { V2TextAreaComponent } from '../text-area/v2-text-area.component';
import { DropdownSearchDirective } from '../dropdown/dropdown-search.directive';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-confirmation-dialog',
  templateUrl: './confirmation-dialog.component.html',
  styleUrls: ['./confirmation-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TemplateWithValueDirective,
    FormsModule,
    ReactiveFormsModule,
    V2DateInputComponent,
    V2ButtonComponent,
    V2CheckboxComponent,
    V2DialogModule,
    V2DropdownComponent,
    TranslateModule,
    MatIcon,
    V2TextAreaComponent,
    DropdownSearchDirective
  ]
})
export class ConfirmationDialogComponent<T = string> implements OnInit {
  data = inject<ConfirmationDialogData<T>>(MAT_DIALOG_DATA);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  dialogRef = inject<MatDialogRef<ConfirmationDialogComponent<T>, ConfirmationDialogConfirm<T>>>(MatDialogRef);

  providedInput: T;
  selectedDropdownValue: T;
  checkboxChecked = false;
  width: string | number = '400px';
  dropdownValues: {
    visible: boolean;
    value: T;
  }[] = [];

  get cancelButtonIcon(): string {
    return this.data?.cancelBtnIcon || 'close';
  }

  get confirmButtonIcon(): string {
    return this.data?.confirmBtnIcon || 'check';
  }

  get thirdButtonIcon(): string | undefined {
    return this.data?.thirdButtonIcon;
  }

  get confirmDisabled(): boolean {
    if (this.data?.provideCheckbox && this.data?.checkboxMandatory && !this.checkboxChecked) {
      return true;
    }
    if (this.data?.inputMandatory) {
      return !(this.data.dateInput ? this.providedInput : (this.providedInput as string)?.trim());
    }
    return this.data?.disableConfirmButton ?? false;
  }

  getTranslatedValue(text: string): string {
    const translatedText = text ? this.translateService.instant(text || '', this.data?.msgInterpolationParams) : '';
    const containsMsgValue = translatedText.includes('{msgValue}');
    return containsMsgValue ? translatedText.replace('{msgValue}', this.data?.msgValue?.toString()) : translatedText;
  }

  getHeadlineData(): string {
    return this.data?.headlineData ? `- ${this.data?.headlineData}` : '';
  }

  public static openDialog<T = string>(
    dialog: MatDialog,
    data: ConfirmationDialogData<T>,
    maxWidth = 'unset',
    disableClose = true
  ) {
    return dialog
      .open<ConfirmationDialogComponent<T>, ConfirmationDialogData<T>, ConfirmationDialogConfirm<T> | undefined>(
        ConfirmationDialogComponent,
        {
          disableClose,
          maxWidth,
          data
        }
      )
      .beforeClosed();
  }

  public ngOnInit(): void {
    this.dialogRef.addPanelClass?.('d1r-dialog-container');
    this.selectedDropdownValue = this?.data?.currentDropdownValue ?? undefined;
    this.providedInput = this.data?.currentInputValue;
    this.width = this.data.width || this.width;
  }

  dropdownDisplayFn(text: T): string {
    return TinyHelpers.pascalcaseToText(text as string);
  }

  public cancel(): void {
    if (this.data.confirmCancelIfEdited) {
      this.dialog
        .open<ConfirmationDialogComponent, ConfirmationDialogData, ConfirmationDialogConfirm | undefined>(
          ConfirmationDialogComponent,
          {
            disableClose: true,
            minWidth: '375px',
            data: {
              headline: 'general.confirmation-dialog.unsaved-changes-confirmation-headline',
              translate: true,
              msg: 'general.confirmation-dialog.unsaved-changes-confirmation-msg',
              confirmMsg: 'general.yes-close',
              cancelMsg: 'general.keep-editing'
            }
          }
        )
        .afterClosed()
        .pipe(
          filter(confirmation => !!confirmation),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => this.dialogRef.close());
    } else {
      this.dialogRef.close();
    }
  }

  public confirm(): void {
    this.dialogRef.close({
      input: this.providedInput,
      dateInputs: this.data?.dateInputs,
      dropdownValue: this.selectedDropdownValue,
      checkboxChecked: this.checkboxChecked,
      clickedButton: 'Confirm'
    });
  }

  public onThirdButtonClick(): void {
    this.dialogRef.close({
      input: this.providedInput,
      dropdownValue: this.selectedDropdownValue,
      clickedButton: 'ThirdButton'
    });
  }
}
